import { ComKey, Item, PriKey } from "@fjell/types";
import { AsyncLocalStorage } from "async_hooks";
import logger from "../logger";

export type ItemKey = PriKey<any> | ComKey<any, any, any, any, any, any>;

/**
 * OperationContext for managing state across asynchronous operations.
 * Used to:
 * - Detect circular dependencies during reference/aggregation loading
 * - Cache loaded items to avoid duplicate work
 * - Share state across nested operations
 */
export interface OperationContext {
  /**
   * Set of serialized keys that are currently being processed.
   * Used to detect circular dependencies.
   */
  inProgress: Set<string>;

  /**
   * Cache of fully loaded objects keyed by serialized key.
   * Used to avoid duplicate work and provide already-loaded objects.
   * Can store individual items, arrays, or any other values.
   */
  cache: Map<string, any>;

  /**
   * Add a key to the in-progress set
   */
  markInProgress(key: ItemKey): void;

  /**
   * Remove a key from the in-progress set
   */
  markComplete(key: ItemKey): void;

  /**
   * Check if a key is currently being processed (cycle detection)
   */
  isInProgress(key: ItemKey): boolean;

  /**
   * Get a cached object by key
   */
  getCached(key: ItemKey): Item<any, any, any, any, any, any> | undefined;

  /**
   * Cache a loaded object by key
   */
  setCached(key: ItemKey, item: Item<any, any, any, any, any, any>): void;

  /**
   * Check if an object is cached
   */
  isCached(key: ItemKey): boolean;
}

/**
 * @deprecated Use OperationContext instead
 */
export type ProcessingContext = OperationContext;

/**
 * Serialize an ItemKey to a string for use in sets and maps
 */
export const serializeKey = (key: ItemKey): string => {
  if (key && 'pk' in key && 'kt' in key && !('loc' in key)) {
    // PriKey
    return `${key.kt}:${key.pk}`;
  } else if (key && 'pk' in key && 'kt' in key && 'loc' in key && key.loc) {
    // ComKey
    const locStr = key.loc.map(l => `${l.kt}:${l.lk}`).join(',');
    return `${key.kt}:${key.pk}|${locStr}`;
  }
  throw new Error(`Unsupported key type: ${JSON.stringify(key)}`);
};

/**
 * Create a new OperationContext
 */
export const createOperationContext = (): OperationContext => {
  const inProgress = new Set<string>();
  const cache = new Map<string, any>();

  return {
    inProgress,
    cache,

    markInProgress(key: ItemKey): void {
      const serialized = serializeKey(key);
      logger.debug('Marking key as in progress', { key, serialized });
      inProgress.add(serialized);
    },

    markComplete(key: ItemKey): void {
      const serialized = serializeKey(key);
      logger.debug('Marking key as complete', { key, serialized });
      inProgress.delete(serialized);
    },

    isInProgress(key: ItemKey): boolean {
      const serialized = serializeKey(key);
      const result = inProgress.has(serialized);
      logger.debug('Checking if key is in progress', { key, serialized, result });
      return result;
    },

    getCached(key: ItemKey): Item<any, any, any, any, any, any> | undefined {
      const serialized = serializeKey(key);
      const result = cache.get(serialized);
      logger.debug('Getting cached item', { key, serialized, found: !!result });
      return result;
    },

    setCached(key: ItemKey, item: Item<any, any, any, any, any, any>): void {
      const serialized = serializeKey(key);
      logger.debug('Caching item', { key, serialized });
      cache.set(serialized, item);
    },

    isCached(key: ItemKey): boolean {
      const serialized = serializeKey(key);
      const result = cache.has(serialized);
      logger.debug('Checking if key is cached', { key, serialized, result });
      return result;
    }
  };
};

/**
 * @deprecated Use createOperationContext instead
 */
export const createProcessingContext = createOperationContext;

/**
 * Context Manager for sharing context across operations without changing public interfaces
 * Uses AsyncLocalStorage to properly maintain context across async boundaries
 */
class ContextManager {
  private asyncLocalStorage = new AsyncLocalStorage<OperationContext>();

  /**
   * Get the current context if one is set
   */
  getCurrentContext(): OperationContext | undefined {
    const context = this.asyncLocalStorage.getStore();
    if (context) {
      logger.debug('Got current context from AsyncLocalStorage');
    }
    return context;
  }

  /**
   * Execute a function with a specific context set as current
   * The context will be available to all async operations within the function
   */
  async withContext<T>(context: OperationContext, fn: () => Promise<T>): Promise<T> {
    logger.debug('Running with context in AsyncLocalStorage');
    return this.asyncLocalStorage.run(context, fn);
  }
}

// Global context manager instance
export const contextManager = new ContextManager();

